import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { AlertCircle, CheckCircle, Wrench, Activity, Droplets, Shield, ArrowLeft, LogIn } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MaintenanceForm() {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuth();
  
  const [equipment, setEquipment] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Operating loads and cycles
    total_cycles: '',
    tonnage: '',
    stroke_rate: '',
    cycles_since_last_maintenance: '',
    
    // Surface condition
    surface_finish: '',
    roughness_level: '',
    coating_type: '',
    coating_thickness_um: '',
    coating_condition: '',
    surface_notes: '',
    
    // Lubrication
    lubricant_type: '',
    application_method: '',
    flow_rate_ml_min: '',
    contamination_level: '',
    lubrication_notes: '',
    
    // General
    maintenance_type: 'routine',
    work_performed: '',
    issues_found: '',
    recommendations: '',
  });
  
  // Fetch equipment info (public endpoint)
  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const res = await fetch(`${API_URL}/api/maintenance/nfc/${equipmentId}`);
        const data = await res.json();
        setEquipment(data);
        
        // Pre-fill current cycles if available
        if (data.current_cycles) {
          setFormData(prev => ({ ...prev, total_cycles: data.current_cycles.toString() }));
        }
      } catch (err) {
        console.error('Failed to fetch equipment:', err);
        setError('Failed to load equipment information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEquipment();
  }, [equipmentId]);
  
  // Fetch predictions if authenticated
  useEffect(() => {
    const fetchPredictions = async () => {
      if (!isAuthenticated || !token || !equipment?.found) return;
      
      try {
        const res = await fetch(`${API_URL}/api/maintenance/predictions/${equipmentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPrediction(data);
        }
      } catch (err) {
        console.error('Failed to fetch predictions:', err);
      }
    };
    
    fetchPredictions();
  }, [isAuthenticated, token, equipmentId, equipment]);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setError('Please log in to submit maintenance data');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Prepare payload - only include non-empty fields
      const payload = {
        equipment_id: equipmentId,
        tour_id: equipment?.tour_id || '',
        maintenance_type: formData.maintenance_type,
      };
      
      // Add numeric fields if provided
      if (formData.total_cycles) payload.total_cycles = parseInt(formData.total_cycles);
      if (formData.tonnage) payload.tonnage = parseFloat(formData.tonnage);
      if (formData.stroke_rate) payload.stroke_rate = parseFloat(formData.stroke_rate);
      if (formData.cycles_since_last_maintenance) payload.cycles_since_last_maintenance = parseInt(formData.cycles_since_last_maintenance);
      if (formData.coating_thickness_um) payload.coating_thickness_um = parseFloat(formData.coating_thickness_um);
      if (formData.flow_rate_ml_min) payload.flow_rate_ml_min = parseFloat(formData.flow_rate_ml_min);
      
      // Add string fields if provided
      if (formData.surface_finish) payload.surface_finish = formData.surface_finish;
      if (formData.roughness_level) payload.roughness_level = formData.roughness_level;
      if (formData.coating_type) payload.coating_type = formData.coating_type;
      if (formData.coating_condition) payload.coating_condition = formData.coating_condition;
      if (formData.surface_notes) payload.surface_notes = formData.surface_notes;
      if (formData.lubricant_type) payload.lubricant_type = formData.lubricant_type;
      if (formData.application_method) payload.application_method = formData.application_method;
      if (formData.contamination_level) payload.contamination_level = formData.contamination_level;
      if (formData.lubrication_notes) payload.lubrication_notes = formData.lubrication_notes;
      if (formData.work_performed) payload.work_performed = formData.work_performed;
      if (formData.issues_found) payload.issues_found = formData.issues_found;
      if (formData.recommendations) payload.recommendations = formData.recommendations;
      
      const res = await fetch(`${API_URL}/api/maintenance/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        throw new Error('Failed to submit maintenance log');
      }
      
      setSuccess(true);
      
      // Refresh predictions
      const predRes = await fetch(`${API_URL}/api/maintenance/predictions/${equipmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (predRes.ok) {
        setPrediction(await predRes.json());
      }
      
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message || 'Failed to submit maintenance log');
    } finally {
      setSubmitting(false);
    }
  };
  
  const getRiskBadge = (level) => {
    const colors = {
      low: 'bg-green-500',
      medium: 'bg-yellow-500',
      high: 'bg-orange-500',
      critical: 'bg-red-500'
    };
    return <Badge className={`${colors[level] || 'bg-gray-500'} text-white`}>{level?.toUpperCase()}</Badge>;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-white text-lg">Loading equipment information...</div>
      </div>
    );
  }
  
  if (!equipment?.found) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-red-400 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Equipment Not Found
            </CardTitle>
            <CardDescription className="text-slate-400">
              Equipment ID: {equipmentId}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-slate-300 mb-4">
              This equipment has not been registered in the system. Please contact an administrator to set up this equipment first.
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                {equipment.equipment_name}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Home
              </Button>
            </div>
            <CardDescription className="text-slate-400">
              Maintenance Log Entry
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <span className="text-slate-300">Current Cycles:</span>
              <span className="text-2xl font-bold text-blue-400">{equipment.current_cycles?.toLocaleString() || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <span className="text-slate-300">Risk Level:</span>
              {getRiskBadge(equipment.risk_level)}
            </div>
            <div className="border-t border-slate-700 pt-4">
              <p className="text-slate-400 mb-4">Please log in to record maintenance data.</p>
              <Button className="w-full" onClick={() => navigate(`/?redirect=/maintenance/${equipmentId}`)}>
                <LogIn className="h-4 w-4 mr-2" />
                Log In to Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-green-400 flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Maintenance Logged Successfully
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-300">
              Your maintenance entry has been recorded for {equipment.equipment_name}.
            </p>
            {prediction && (
              <div className="p-4 bg-slate-700/50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Updated Cycles:</span>
                  <span className="text-blue-400 font-bold">{prediction.current_cycles?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Risk Level:</span>
                  {getRiskBadge(prediction.risk_level)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Life Used:</span>
                  <span className="text-white">{prediction.percent_life_used}%</span>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSuccess(false)} className="flex-1">
                Log Another Entry
              </Button>
              <Button onClick={() => navigate('/')} className="flex-1">
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-900 p-4 pb-20">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                {equipment.equipment_name}
              </CardTitle>
              <div className="flex items-center gap-2">
                {getRiskBadge(equipment.risk_level)}
                <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Home
                </Button>
              </div>
            </div>
            <CardDescription className="text-slate-400">
              Logged in as: {user?.name || user?.email}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-700/50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-400">{equipment.current_cycles?.toLocaleString() || 0}</div>
                <div className="text-xs text-slate-400">Current Cycles</div>
              </div>
              {prediction && (
                <div className="p-3 bg-slate-700/50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-400">{prediction.percent_life_used}%</div>
                  <div className="text-xs text-slate-400">Life Used</div>
                </div>
              )}
            </div>
            
            {/* Alert Message */}
            {prediction?.alert_message && (
              <div className={`mt-4 p-3 rounded-lg flex items-start gap-2 ${
                prediction.risk_level === 'critical' ? 'bg-red-500/20 text-red-300' :
                prediction.risk_level === 'high' ? 'bg-orange-500/20 text-orange-300' :
                'bg-blue-500/20 text-blue-300'
              }`}>
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{prediction.alert_message}</span>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Operating Loads & Cycles */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Operating Loads & Cycles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Total Cycles *</Label>
                  <Input
                    type="number"
                    value={formData.total_cycles}
                    onChange={(e) => handleInputChange('total_cycles', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="e.g., 125000"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Tonnage</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.tonnage}
                    onChange={(e) => handleInputChange('tonnage', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="tons"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Stroke Rate</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.stroke_rate}
                    onChange={(e) => handleInputChange('stroke_rate', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="per min"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Since Last Maint.</Label>
                  <Input
                    type="number"
                    value={formData.cycles_since_last_maintenance}
                    onChange={(e) => handleInputChange('cycles_since_last_maintenance', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="cycles"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Surface Condition */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Surface Condition
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Surface Finish</Label>
                  <Select value={formData.surface_finish} onValueChange={(v) => handleInputChange('surface_finish', v)}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Good">Good</SelectItem>
                      <SelectItem value="Fair">Fair</SelectItem>
                      <SelectItem value="Poor">Poor</SelectItem>
                      <SelectItem value="Critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Roughness</Label>
                  <Select value={formData.roughness_level} onValueChange={(v) => handleInputChange('roughness_level', v)}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Coating Type</Label>
                  <Select value={formData.coating_type} onValueChange={(v) => handleInputChange('coating_type', v)}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TiN">TiN</SelectItem>
                      <SelectItem value="DLC">DLC</SelectItem>
                      <SelectItem value="CrN">CrN</SelectItem>
                      <SelectItem value="None">None</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Coating Condition</Label>
                  <Select value={formData.coating_condition} onValueChange={(v) => handleInputChange('coating_condition', v)}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Intact">Intact</SelectItem>
                      <SelectItem value="Worn">Worn</SelectItem>
                      <SelectItem value="Flaking">Flaking</SelectItem>
                      <SelectItem value="Failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Surface Notes</Label>
                <Textarea
                  value={formData.surface_notes}
                  onChange={(e) => handleInputChange('surface_notes', e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Any observations about surface condition..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Lubrication */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Droplets className="h-4 w-4" />
                Lubrication
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Lubricant Type</Label>
                  <Input
                    value={formData.lubricant_type}
                    onChange={(e) => handleInputChange('lubricant_type', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="e.g., Synthetic Oil"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Application Method</Label>
                  <Select value={formData.application_method} onValueChange={(v) => handleInputChange('application_method', v)}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Spray">Spray</SelectItem>
                      <SelectItem value="Drip">Drip</SelectItem>
                      <SelectItem value="Immersion">Immersion</SelectItem>
                      <SelectItem value="Manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Flow Rate (ml/min)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.flow_rate_ml_min}
                    onChange={(e) => handleInputChange('flow_rate_ml_min', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="ml/min"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Contamination</Label>
                  <Select value={formData.contamination_level} onValueChange={(v) => handleInputChange('contamination_level', v)}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Clean">Clean</SelectItem>
                      <SelectItem value="Slight">Slight</SelectItem>
                      <SelectItem value="Moderate">Moderate</SelectItem>
                      <SelectItem value="Heavy">Heavy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Lubrication Notes</Label>
                <Textarea
                  value={formData.lubrication_notes}
                  onChange={(e) => handleInputChange('lubrication_notes', e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Any observations about lubrication..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Work Performed */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg">Maintenance Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300">Maintenance Type</Label>
                <Select value={formData.maintenance_type} onValueChange={(v) => handleInputChange('maintenance_type', v)}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine Inspection</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="inspection">Detailed Inspection</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Work Performed</Label>
                <Textarea
                  value={formData.work_performed}
                  onChange={(e) => handleInputChange('work_performed', e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Describe the work performed..."
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-slate-300">Issues Found</Label>
                <Textarea
                  value={formData.issues_found}
                  onChange={(e) => handleInputChange('issues_found', e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Any issues discovered..."
                  rows={2}
                />
              </div>
              <div>
                <Label className="text-slate-300">Recommendations</Label>
                <Textarea
                  value={formData.recommendations}
                  onChange={(e) => handleInputChange('recommendations', e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Recommendations for future maintenance..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          
          {/* Submit */}
          <Button 
            type="submit" 
            className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-500"
            disabled={submitting || !formData.total_cycles}
          >
            {submitting ? 'Submitting...' : 'Submit Maintenance Log'}
          </Button>
        </form>
      </div>
    </div>
  );
}
